/**
 * The seller's notes on a finished run: was it useful (with a comment) and what came of it. Whoever started the run
 * edits them; anyone else who reads the run sees them as text.
 */

import { useState } from 'react';
import { Save } from 'lucide-react';

import { RunPatch, runPatchFeedbackCommentOneMax } from '@/api/generated/schemas';
import { useUpdateAdvisorRun } from '@/api/queries';
import type { AdvisorFeedback, AdvisorOutcome, AdvisorRun } from '@/api/types';
import { bannerError, fieldErrors, serverFieldErrors } from '@/api/validate';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { FieldError, fieldA11y } from '@/ui/FieldError';
import { FormError } from '@/ui/StateCard';

import { OUTCOME_LABELS } from './advisorUi';

const FEEDBACK_LABELS: Record<AdvisorFeedback, string> = { useful: 'Useful', not_useful: 'Not useful' };

function ReadOnly({ run }: { run: AdvisorRun }) {
  return (
    <div className="stack-sm">
      <p>Feedback: {run.feedback ? FEEDBACK_LABELS[run.feedback] : 'Not given'}{run.feedback_comment ? `. ${run.feedback_comment}` : ''}</p>
      <p>Outcome: {run.outcome ? OUTCOME_LABELS[run.outcome] : 'Not recorded'}</p>
    </div>
  );
}

function Editor({ run }: { run: AdvisorRun }) {
  const save = useUpdateAdvisorRun(run.id);
  const [feedback, setFeedback] = useState<AdvisorFeedback | null>(run.feedback);
  const [comment, setComment] = useState(run.feedback_comment ?? '');
  const [outcome, setOutcome] = useState<AdvisorOutcome | ''>(run.outcome ?? '');
  const invalid = Object.keys(fieldErrors(RunPatch.pick({ feedback_comment: true }), { feedback_comment: comment })).length > 0;
  const server = serverFieldErrors(save.error);
  return (
    <form
      className="stack-md"
      onSubmit={(e) => {
        e.preventDefault();
        if (invalid) return;
        save.mutate({ feedback, feedback_comment: comment.trim() || null, outcome: outcome || null });
      }}
    >
      <FormError error={bannerError(save.error, ['feedback_comment'])} />
      <fieldset className="field-group">
        <legend className="label">Was this run useful?</legend>
        <div className="row">
          {(Object.keys(FEEDBACK_LABELS) as AdvisorFeedback[]).map((value) => (
            <label key={value} className="check">
              <input type="radio" name="advisor-feedback" checked={feedback === value} onChange={() => setFeedback(value)} />
              {FEEDBACK_LABELS[value]}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="field">
        <label htmlFor="advisor-comment">Comment (optional)</label>
        <textarea id="advisor-comment" rows={3} maxLength={runPatchFeedbackCommentOneMax} value={comment}
          placeholder="What was missing, or what helped" {...fieldA11y('advisor-comment', server.feedback_comment)} onChange={(e) => setComment(e.target.value)} />
        <FieldError inputId="advisor-comment" message={server.feedback_comment} />
      </div>
      <div className="field">
        <label htmlFor="advisor-outcome">Outcome</label>
        <select id="advisor-outcome" value={outcome} onChange={(e) => setOutcome(e.target.value as AdvisorOutcome | '')}>
          <option value="">Not recorded yet</option>
          {(Object.keys(OUTCOME_LABELS) as AdvisorOutcome[]).map((value) => (
            <option key={value} value={value}>{OUTCOME_LABELS[value]}</option>
          ))}
        </select>
      </div>
      <div className="row">
        <ActionButton state={buttonState(save)} icon={<Save aria-hidden="true" />} label="Save feedback" disabled={invalid} />
      </div>
    </form>
  );
}

export function FeedbackCard({ run }: { run: AdvisorRun }) {
  return (
    <section className="card" aria-labelledby="advisor-feedback-title">
      <div className="card-body stack-md">
        <h2 id="advisor-feedback-title" className="advisor-title">Feedback and outcome</h2>
        {run.can.edit ? <Editor run={run} /> : <ReadOnly run={run} />}
      </div>
    </section>
  );
}
